class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    // Paired with the rules in snippets/cart-drawer.liquid that hide the
    // floating app widgets (AccessEase, Retentionly WhatsApp) while the drawer
    // is open. It goes on <html> rather than <body> so it is set before any
    // app can reparent its launcher, and it is a plain class so the CSS needs
    // no :has() support. Purely a styling hook - nothing else reads it.
    document.documentElement.classList.add('cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // renderContents only ever runs after a successful add-to-cart, so the
    // cart is guaranteed non-empty at this point. (The previous check read
    // '.drawer__inner' for an 'is-empty' class that is only ever set on the
    // outer <cart-drawer> element, so it never matched and never cleared.)
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // There is no separate remove/trash button in the drawer - reaching 0 on
  // the quantity stepper IS the removal action, so it must bypass the
  // quantity_rule.min check below (which would otherwise block going below
  // the product's minimum order quantity, typically 1, and bounce back).
  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    if (inputValue === 0) {
      event.target.setCustomValidity('');
      this.updateQuantity(
        event.target.dataset.index,
        0,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
      return;
    }
    super.validateQuantity(event);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerDiscount extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.input = this.form.querySelector('input[name="discount"]');
    this.message = this.querySelector('[data-discount-message]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  onSubmit(event) {
    event.preventDefault();
    const code = this.input.value.trim();
    if (!code) return;

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.message.hidden = true;

    const body = JSON.stringify({
      discount: code,
      sections: ['cart-drawer'],
      sections_url: window.location.pathname,
    });

    // /cart/update.js's `discount` parameter applies a code directly to the
    // cart (Shopify Editions '25) - the response's discount_codes array
    // reports per-code applicability, which is the only reliable way to
    // distinguish a valid code from an invalid one.
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        const submittedCode = (parsedState.discount_codes || []).find(
          (entry) => entry.code.toLowerCase() === code.toLowerCase()
        );

        if (submittedCode && submittedCode.applicable) {
          const targetInner = document.querySelector('#CartDrawer .drawer__inner');
          const sectionHtml = parsedState.sections && parsedState.sections['cart-drawer'];
          if (targetInner && sectionHtml) {
            const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const sourceInner = html.querySelector('#CartDrawer .drawer__inner');
            if (sourceInner) targetInner.innerHTML = sourceInner.innerHTML;
          }
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          this.message.hidden = false;
          this.message.textContent = 'קוד ההנחה אינו תקף';
        }
      })
      .catch(() => {
        this.submitButton.removeAttribute('aria-disabled');
        this.message.hidden = false;
        this.message.textContent = 'שגיאה בהחלת הקוד, נסו שוב';
      });
  }
}

customElements.define('cart-drawer-discount', CartDrawerDiscount);
